// The questionnaire a client opens from the link the office sent. No firm
// chrome, no staff account: the token in the URL becomes a day's session,
// answers save as the client goes, and submit hands the whole thing to
// the office. The whole page speaks one language; every label falls back
// to its English on its own.

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import type { IntakeQuestionnaireState } from "@duralex/shared";
import {
  clearIntakeLinkToken,
  deleteIntakeDocument,
  fetchIntakeState,
  getIntakeLinkToken,
  IntakeLinkApiError,
  openIntakeLink,
  saveIntakeAnswers,
  setIntakeLinkToken,
  submitIntake,
  uploadIntakeDocument,
  type SaveIntakeInput,
} from "../api/intakeLink";
import { Skeleton, SkeletonGroup } from "../../../components/chrome/Skeleton";
import { QuestionnaireForm, type FieldNote } from "../components/QuestionnaireForm";
import { useIntakeLocale } from "../hooks/useIntakeLocale";
import { useIntakeWords } from "../hooks/useIntakeWords";

type Phase = "boot" | "form" | "invalid" | "error" | "done";

const keyOf = (vars: SaveIntakeInput): string =>
  vars.answers ? (Object.keys(vars.answers)[0] ?? "") : (Object.keys(vars)[0] ?? "");

export default function IntakeStart() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const urlToken = params.get("t");

  const [phase, setPhase] = useState<Phase>("boot");
  // Not react-query cache: this page is its own world, and every write
  // answers with the next state - the mutations ARE the fetches.
  const [form, setForm] = useState<IntakeQuestionnaireState | null>(null);
  // Ticks the server has not echoed back yet: a tick must show at once,
  // and the next one must build on it.
  const [multiDrafts, setMultiDrafts] = useState<Record<string, string[]>>({});
  const [notes, setNotes] = useState<Record<string, FieldNote>>({});
  // Only the newest save's response may become the form: an older body
  // landing late would revert newer answers.
  const saveSeq = useRef(0);

  // One language on the whole page: the client's choice, else the
  // session's, else the browser's, else English. The words are not asked
  // for before /open names the session's language - an English frame
  // under Russian questions is the pair this page must never show.
  const { locale, choose } = useIntakeLocale(form?.language, form?.languages);
  const { words: uiMap, t, spokenIn, isError, refetch } = useIntakeWords(locale, phase !== "boot");

  const arrive = (s: IntakeQuestionnaireState) => {
    setForm(s);
    setPhase("form");
  };

  // 401/404/410 is the server calling the link dead; anything else - a
  // burst limit, a 500, no network - is a bad moment, not a bad link.
  const bootFailed = (err: unknown) => {
    const dead = err instanceof IntakeLinkApiError && [401, 404, 410].includes(err.status);
    setPhase(dead ? "invalid" : "error");
  };

  const boot = () => {
    setPhase("boot");
    if (urlToken) {
      openIntakeLink(urlToken).then((r) => {
        setIntakeLinkToken(r.token);
        arrive(r.questionnaire);
        // The one-time token must not linger in the address bar or history.
        navigate("/intake/start", { replace: true });
      }, bootFailed);
    } else if (getIntakeLinkToken()) {
      fetchIntakeState().then(arrive, bootFailed);
    } else {
      setPhase("invalid");
    }
  };

  const opened = useRef(false);
  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    boot();
  }, []);

  const gone = (err: unknown) => {
    if (err instanceof IntakeLinkApiError && err.status === 401) setPhase("invalid");
  };

  const save = useMutation({
    mutationFn: saveIntakeAnswers,
    onMutate: (vars) => {
      setNotes((n) => {
        const next = { ...n };
        delete next[keyOf(vars)];
        return next;
      });
      return { seq: ++saveSeq.current };
    },
    onSuccess: (s, vars, ctx) => {
      if (ctx && ctx.seq === saveSeq.current) setForm(s);
      setNotes((n) => ({ ...n, [keyOf(vars)]: { kind: "saved" } }));
    },
    onError: (err, vars) => {
      gone(err);
      const key = keyOf(vars);
      // Fall back to the server's truth for the boxes we guessed at.
      setMultiDrafts((d) => {
        const next = { ...d };
        delete next[key];
        return next;
      });
      setNotes((n) => ({ ...n, [key]: { kind: "error", message: err.message } }));
    },
  });

  const upload = useMutation({
    mutationFn: uploadIntakeDocument,
    onSuccess: (s) => setForm(s),
    onError: gone,
  });

  const removeDoc = useMutation({
    mutationFn: deleteIntakeDocument,
    onSuccess: (s) => setForm(s),
    onError: gone,
  });

  const submit = useMutation({
    mutationFn: submitIntake,
    onSuccess: () => {
      clearIntakeLinkToken();
      setPhase("done");
    },
    onError: gone,
  });

  if (phase === "invalid") {
    return (
      <main className="iform-note" lang={spokenIn}>
        <div className="iform-note-card">
          <h1>{t("invalid_title")}</h1>
          <p>{t("invalid_text")}</p>
        </div>
      </main>
    );
  }
  if (phase === "done") {
    return (
      <main className="iform-note" lang={spokenIn}>
        <div className="iform-note-card">
          <h1>{t("done_title")}</h1>
          <p>{t("done_text")}</p>
        </div>
      </main>
    );
  }
  // A form without its words is no form: a failed /ui gets the same
  // honest screen and the same Try again as a failed boot. A switch whose
  // new words failed keeps the old ones - the form stays.
  if (phase === "error" || (isError && !uiMap)) {
    return (
      <main className="iform-note" lang={spokenIn}>
        <div className="iform-note-card">
          <h1>{t("error_title")}</h1>
          <p>{t("error_text")}</p>
          <button
            type="button"
            className="cc-btn-primary"
            onClick={() => {
              if (isError) void refetch();
              if (phase === "error") boot();
            }}
          >
            {t("error_retry")}
          </button>
        </div>
      </main>
    );
  }
  if (phase === "boot" || !form || !uiMap) {
    return (
      <main className="iform" lang={spokenIn}>
        <div className="iform-card">
          <SkeletonGroup label="Loading the questionnaire">
            <Skeleton width="45%" />
            <Skeleton width="80%" />
            <Skeleton width="70%" />
          </SkeletonGroup>
        </div>
      </main>
    );
  }

  const alerts = {
    upload: upload.isError ? (upload.error as Error).message : undefined,
    remove: removeDoc.isError ? (removeDoc.error as Error).message : undefined,
    submit: submit.isError ? (submit.error as Error).message : undefined,
  };

  return (
    <main className="iform" lang={locale}>
      <QuestionnaireForm
        template={form.template}
        locale={locale}
        languages={form.languages}
        t={t}
        category={form.category}
        categoryLocked={form.categorySource === "office"}
        answers={form.answers}
        ticks={multiDrafts}
        documents={form.documents}
        outstanding={form.outstanding}
        notes={notes}
        expiresAt={form.expiresAt}
        saving={save.isPending}
        uploading={upload.isPending}
        removing={removeDoc.isPending}
        submitting={submit.isPending}
        alerts={alerts}
        onLanguage={(l) => {
          choose(l);
          save.mutate({ language: l });
        }}
        onCategory={(code) => save.mutate({ category: code })}
        onAnswer={(id, value) => {
          if (Array.isArray(value)) setMultiDrafts((d) => ({ ...d, [id]: value }));
          save.mutate({ answers: { [id]: value } });
        }}
        onUpload={(item, file) => upload.mutate({ item, file })}
        onRemoveDoc={(id) => removeDoc.mutate(id)}
        onSubmit={(consents) => submit.mutate(consents)}
      />
    </main>
  );
}
